import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { Translate } from 'react-localize-redux';
import css from './UserConfigurationShelf.css';
import { Shelf, Button, Spinner, TextField, Table, TableCell, WarningMessage } from '../../../components/widgets';
import TableCellButton from '../../../components/widgets/TableCellButton';
import { GET_PARTNER_ACCOUNTS } from '../../queries';
import { partnerShape } from '../types';
import useOAuthFlow from '../../hooks/useOAuthFlow';

export const USER_CONFIGURATION_SHELF_DATA_TEST_ID = 'user-configuration-shelf';
export const ACCOUNTS_TABLE_DATA_TEST_ID = 'user-configuration-accounts-table';

const AccountField = {
    EMAIL: 'loginId',
    REAUTHORIZE: 'reauthorize',
};

export default function UserConfigurationShelf({ open = false, partner, onClose = () => {} }) {
    const { id: partnerId, name: partnerName, displayName: partnerDisplayName, icon } = partner;

    const [searchText, setSearchText] = useState('');
    const [sortDirection, setSortDirection] = useState(null);

    const { startOAuthFlow, error: oauthError, clearError } = useOAuthFlow(partnerId, partnerName, partnerDisplayName);

    const partnerAccountsResponse = useQuery(GET_PARTNER_ACCOUNTS, {
        variables: { partnerId },
        skip: !open,
    });

    const accounts = partnerAccountsResponse.data?.partnerAccounts || [];

    const filteredAccounts = useMemo(() => {
        if (!searchText) return accounts;
        const lowerSearch = searchText.toLowerCase();
        return accounts.filter(account => account.loginId?.toLowerCase().includes(lowerSearch));
    }, [accounts, searchText]);

    const sortedAccounts = useMemo(() => {
        if (sortDirection === null) return filteredAccounts;
        return [...filteredAccounts].sort((a, b) => {
            const aVal = a.loginId?.toLowerCase() || '';
            const bVal = b.loginId?.toLowerCase() || '';
            if (sortDirection === 'asc') return aVal.localeCompare(bVal);
            return bVal.localeCompare(aVal);
        });
    }, [filteredAccounts, sortDirection]);

    const triggerOAuthFlow = () => {
        clearError();
        startOAuthFlow();
    };

    const tableMetadata = [
        {
            name: AccountField.EMAIL,
            displayName: 'STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION_SHELF.EMAIL_COLUMN',
            cellComponent: TableCell,
            headerAlign: 'left',
            boldHeader: true,
            showHeaderArrow: true,
            headerArrow: sortDirection === null ? null : sortDirection === 'asc' ? 'up' : 'down',
            headerClick: () => {
                setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
            },
        },
        {
            name: AccountField.REAUTHORIZE,
            displayName: '',
            cellComponent: TableCellButton,
            headerAlign: 'right',
            cellProps: {
                translationId: 'STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION_SHELF.REAUTHORIZE',
                type: 'secondary',
                onClick: triggerOAuthFlow,
                tdClassName: css.reauthorizeCell,
            },
        },
    ];

    const footerButtons = [
        {
            type: 'primaryButton',
            text: 'STATIC.BUTTONS.DONE',
            onClick: onClose,
        },
    ];

    const getShelfContent = () => {
        if (partnerAccountsResponse.loading) {
            return <Spinner show expanded />;
        }

        if (partnerAccountsResponse.error) {
            return (
                <WarningMessage
                    show
                    type="error"
                    message="STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION_SHELF.LOAD_ERROR"
                    showIcon
                />
            );
        }

        return (
            <div className={css.userConfigurationShelf}>
                <div className={css.headerSection}>
                    <div className={css.headerInfo}>
                        <h3 className={css.title}>
                            <Translate
                                id="STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION_SHELF.TITLE"
                                data={{ partnerName: partnerDisplayName }}
                            />
                        </h3>
                        <p className={css.description}>
                            <Translate
                                id="STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION_SHELF.DESCRIPTION"
                                data={{ partnerName: partnerDisplayName }}
                            />
                        </p>
                    </div>
                    <Button type="primary" onClick={triggerOAuthFlow} className={css.addUserButton}>
                        <Translate
                            id="STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION_SHELF.ADD_USER"
                            data={{ partnerName: partnerDisplayName }}
                        />
                    </Button>
                </div>

                {oauthError && <WarningMessage show type="error" message={oauthError} showIcon />}

                <div className={css.searchSection}>
                    <TextField
                        placeholder="STATIC.PLACEHOLDERS.SEARCH"
                        value={searchText}
                        onChange={value => setSearchText(value)}
                        containerClass={css.searchInput}
                        Icon="search"
                    />
                </div>

                <div className={css.accountsSection}>
                    <div className={css.accountsHeader}>
                        <img src={icon} alt={partnerDisplayName} className={css.partnerLogo} />
                        <span className={css.accountsTitle}>
                            <Translate
                                id="STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION_SHELF.ACCOUNTS_HEADER"
                                data={{ partnerName: partnerDisplayName, count: accounts.length }}
                            />
                        </span>
                    </div>

                    {accounts.length === 0 ? (
                        <div className={css.emptyState}>
                            <Translate id="STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION_SHELF.NO_ACCOUNTS" />
                        </div>
                    ) : filteredAccounts.length === 0 ? (
                        <div className={css.emptyState}>
                            <Translate id="STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION_SHELF.NO_MATCHING_ACCOUNTS" />
                        </div>
                    ) : (
                        <Table
                            dataTestId={ACCOUNTS_TABLE_DATA_TEST_ID}
                            data={sortedAccounts}
                            metadata={tableMetadata}
                            trClass={css.tableRow}
                            theadClass={css.tableHeader}
                            zebraTable={false}
                            rowHoverEnabled={false}
                            showInnerBorder
                        />
                    )}
                </div>
            </div>
        );
    };

    return (
        <Shelf
            open={open}
            shelfSize="large"
            headerText="STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION"
            headerImagePath={icon}
            enterAnimationDisabled={false}
            onClose={onClose}
            dataTestId={USER_CONFIGURATION_SHELF_DATA_TEST_ID}
            footerNavigationButtons={footerButtons}
        >
            {getShelfContent()}
        </Shelf>
    );
}

UserConfigurationShelf.propTypes = {
    open: PropTypes.bool,
    partner: partnerShape.isRequired,
    onClose: PropTypes.func,
};
